import {
  changeLines,
  computeCorrection,
  correctable,
  FinCorrRow,
  nextCorrectiveNumber,
  openTarget,
  r2c,
  reasonLabel,
  signed,
  taxNow,
  zeroRows,
} from 'src/app/pages/finances/data/finances-corrections';
import type { FinCorrectionInput, FinDataset, FinDocument } from 'src/app/pages/finances/data/finances.model';
import { addDays, daysBetween, fmtMoney } from 'src/app/pages/finances/data/finances-format';

/**
 * Corrective invoices on sample data (README WP9): the mockup's `issueCorrection` and `onCorrAction` on the visit's
 * copy of the dataset. Unlike every other sample action these change the documents, so Dan's five "Done when" steps
 * can be followed on the page: the page reads the documents again after each one and sees the corrective invoice,
 * "Corrected by", the credit and the refund, exactly as the server answers them.
 *
 * Each function answers the document the server would answer, or throws a {@link SampleRefusal} (409) when the
 * server would refuse.
 */

/** The server's 409: the action is not possible on that document. */
export class SampleRefusal extends Error {}

const outstanding = (x: FinDocument) => r2c(x.total - x.paid - (x.credited || 0));

function find(data: FinDataset, id: string): FinDocument {
  const d = data.documents.find(x => x.id === id);
  if (!d) throw new SampleRefusal('Not found');
  return d;
}

function contactOf(data: FinDataset, personId: string): string {
  const c = data.contacts.find(x => x.id === personId);
  return c ? c.contact || c.name : 'the client';
}

/** Issues one correction of `d` into the dataset (the mockup's `issueCorrection`) and answers the new document. */
function issue(data: FinDataset, d: FinDocument, input: FinCorrectionInput, cancels: boolean): FinDocument {
  if (!correctable(d, false)) throw new SampleRefusal('Not correctable');
  const method = input.method;
  if (method === 'replace' && d.corrections?.length) throw new SampleRefusal('Already corrected');
  const rows: FinCorrRow[] = cancels ? zeroRows(d) : input.lines.map(r => ({ qty: r.qty ?? NaN, rate: r.rate ?? NaN }));
  // "The tax was wrong" carries the tax as it should be; any other reason keeps the invoice's own.
  const spec = input.tax ? input.tax.lines.map(l => ({ kind: l.kind ?? 'tax', code: l.code, label: l.label, rate: l.rate, baseRatio: l.baseRatio })) : null;
  const x = computeCorrection(d, method, rows, taxNow(data.documents, d), spec);
  if (method !== 'replace' && !x.changed && !cancels) throw new SampleRefusal('Nothing changes');
  const today = data.today;
  const id = nextCorrectiveNumber(data.documents, data.today);
  const openBefore = outstanding(d);
  const terms = daysBetween(d.issueDate, d.dueDate);
  const doc: FinDocument = {
    id, kind: 'invoice', personId: d.personId, projectId: d.projectId, service: d.service, ccy: d.ccy,
    issueDate: today, dueDate: method === 'replace' ? d.dueDate : x.total > 0 ? addDays(today, terms) : today,
    lines: x.lines, subtotal: x.subtotal, discount: x.discount, taxLines: x.taxLines, total: x.total,
    // The new tax's legends; otherwise the original's.
    taxNotes: input.tax ? input.tax.notes.slice() : d.taxNotes?.slice(),
    payments: [], paid: 0, credited: 0, state: 'sent', scheduledDate: null, note: '',
    history: [{ date: today, what: 'Issued and sent to ' + contactOf(data, d.personId) + ' · ' + (method === 'replace' ? 'replaces ' : 'corrects ') + d.id }],
    corrective: {
      of: d.id, ofDate: d.issueDate, method, reason: input.reason, reasonLabel: reasonLabel(input.reason), cancels,
      before: x.before, after: { subtotal: x.after.subtotal, discount: x.after.discount, total: x.after.total }, changes: changeLines(d, x),
      verifactuType: ({ details: 'R4', insolvent: 'R2', uncollectible: 'R3' } as Record<string, string>)[input.reason] ?? 'R1',
      verifactuMethod: method === 'replace' ? 'S' : 'I',
    },
    corrections: [], appliedTo: [], creditsIn: [],
  };
  d.corrections = [...(d.corrections ?? []), id];
  d.history = d.history ?? [];
  if (method === 'replace') {
    // The original's open balance moves to the replacement; what was paid on it counts toward the replacement.
    d.credited = r2c((d.credited || 0) + Math.max(openBefore, 0));
    doc.credited = r2c(d.paid || 0);
    d.replacedBy = id;
    d.history.push({ date: today, what: 'Replaced by ' + id });
  } else {
    if (x.total < 0) {
      // Money taken off goes first to what is still open on the original; the client is owed the rest.
      const applied = r2c(Math.min(-x.total, Math.max(openBefore, 0)));
      d.credited = r2c((d.credited || 0) + applied);
      doc.credited = -applied;
      doc.owedAtIssue = r2c(-x.total - applied);
      if (applied > 0) doc.appliedTo = [{ id: d.id, amount: applied, date: today }];
    }
    d.current = x.after;
    if (cancels) {
      d.cancelledBy = id;
      d.history.push({ date: today, what: 'Cancelled by corrective invoice ' + id + ' (' + fmtMoney(x.total, d.ccy) + ')' });
    } else d.history.push({ date: today, what: 'Corrected by ' + id + ' (' + signed(x.total, d.ccy) + ')' });
  }
  data.documents.push(doc);
  return doc;
}

/** `POST documents/{id}/corrections`. */
export function sampleCorrect(data: FinDataset, id: string, body: unknown): FinDocument {
  const input = body as FinCorrectionInput;
  if (!input || (input.method !== 'difference' && input.method !== 'replace') || !Array.isArray(input.lines)) throw new SampleRefusal('Bad body');
  if (input.tax && input.reason !== 'tax') throw new SampleRefusal('Tax is only sent when the reason is tax');
  return issue(data, find(data, id), input, false);
}

/** `POST documents/{id}/void`: a draft is accepted as before (nothing changes); a sent invoice is cancelled by a corrective. */
export function sampleCancel(data: FinDataset, id: string): unknown {
  const d = find(data, id);
  if (d.state === 'draft' || d.kind !== 'invoice') return { ok: true };
  if (d.cancelledBy) throw new SampleRefusal('Already cancelled');
  return issue(data, d, { method: 'difference', reason: 'returned', lines: [] }, true);
}

/** What a corrective still owes the client (positive); refused when nothing is owed. */
function owedOn(c: FinDocument): number {
  const owed = r2c(-outstanding(c));
  if (!c.corrective || owed <= 0.004) throw new SampleRefusal('Nothing is owed');
  return owed;
}

/** `POST documents/{id}/credits`: takes what the client is owed off their invoice due first (or `targetId`). */
export function sampleApplyCredit(data: FinDataset, id: string, body: unknown): FinDocument {
  const c = find(data, id);
  const owed = owedOn(c);
  const targetId = (body as { targetId?: string } | null)?.targetId;
  const t = targetId ? find(data, targetId) : openTarget(data.documents, c, outstanding);
  if (!t || outstanding(t) <= 0.004) throw new SampleRefusal('Nothing open');
  const amt = r2c(Math.min(owed, outstanding(t)));
  const today = data.today;
  c.credited = r2c((c.credited || 0) - amt);
  t.credited = r2c((t.credited || 0) + amt);
  c.appliedTo = [...(c.appliedTo ?? []), { id: t.id, amount: amt, date: today }];
  t.creditsIn = [...(t.creditsIn ?? []), { id: c.id, amount: amt, date: today }];
  c.history.push({ date: today, what: fmtMoney(amt, c.ccy) + ' taken off ' + t.id });
  t.history.push({ date: today, what: 'Credit of ' + fmtMoney(amt, c.ccy) + ' from ' + c.id + ' taken off' });
  return c;
}

/**
 * `POST documents/{id}/refunds`: a payment out on the corrective, from the account and by the method the client
 * paid the original with (a negative payment, as the server answers it).
 */
export function sampleRefund(data: FinDataset, id: string, body: unknown): FinDocument {
  const c = find(data, id);
  const owed = owedOn(c);
  const b = (body ?? {}) as { amount?: number; date?: string; method?: string; accountId?: string };
  const amount = b.amount && b.amount > 0 ? r2c(Math.min(b.amount, owed)) : owed;
  const orig = data.documents.find(x => x.id === c.corrective?.of);
  const lastP = orig?.payments?.length ? orig.payments[orig.payments.length - 1] : null;
  const method = b.method || lastP?.method || 'Wire transfer';
  const accountId = b.accountId || lastP?.accountId || data.accounts.find(a => a.ccy === c.ccy)?.id || data.accounts[0]?.id || '';
  const date = b.date || data.today;
  c.payments.push({ date, amount: -amount, method, accountId, refund: true });
  c.paid = r2c((c.paid || 0) - amount);
  c.refunded = true;
  c.history.push({ date, what: 'Refund of ' + fmtMoney(amount, c.ccy) + ' sent (' + method + ')' });
  return c;
}

/** `POST documents/{id}/keep-credit`: only when nothing of theirs in that currency is open. */
export function sampleKeepCredit(data: FinDataset, id: string): FinDocument {
  const c = find(data, id);
  owedOn(c);
  if (c.keptAsCredit) throw new SampleRefusal('Already kept');
  if (openTarget(data.documents, c, outstanding)) throw new SampleRefusal('Take it off the open invoice instead');
  c.keptAsCredit = true;
  c.history.push({ date: data.today, what: 'Kept as credit for the next invoice' });
  return c;
}
